// src/pages/Level-4.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LevelTemplate from "./LevelTemplate";
import { TrophyStorage } from "../services/trophyStorage";

/* ─────────────────────────────────────────────
   CONFIG – Level 4 (V1.1)
   - Scene video first, then melody page
   - Streams from Cloudflare Stream
   - Uses your Level 4 hero image
   ───────────────────────────────────────────── */

// ✅ Cloudflare Stream video ID extracted from your manifest URL
// https://customer-.../cb2e9736003ffefa9252f6ce1f926c8d/manifest/video.m3u8
const LEVEL4_STREAM_ID = "cb2e9736003ffefa9252f6ce1f926c8d";

// ✅ LevelTemplate supports: videoSrc="stream:<id>"
const LEVEL4_VIDEO_SRC = `stream:${LEVEL4_STREAM_ID}`;

// ✅ Hero image (place file in: public/images/Level 4 Hero.png)
const LEVEL4_HERO_IMAGE = "/images/" + encodeURIComponent("Level 4 Hero.png");

/* ─────────────────────────────────────────────
   MELODY
   - Same structure as Twinkle
   - Line 4: ✅ added extra F (now 8 notes)
   - Keep lyric grouping aligned to your mapping:
     Bahar-ni      kalo-toh   mar jaye    gi
     [G  G]        [FF  F]    [EE]        [D]
   ───────────────────────────────────────────── */

const LEVEL4_MELODY_LINES = [
  { id: 0, notes: ["C", "C", "G", "G", "A", "A", "G"], lyric: "Machili jal ki rani hai," },
  { id: 1, notes: ["F", "F", "E", "E", "D", "D", "C"], lyric: "Jeevan uska paani hai," },
  { id: 2, notes: ["G", "G", "F", "F", "E", "E", "D"], lyric: "Haath lagao toh dar jayegi," },

  // ✅ UPDATED: extra F added (8 notes total)
  { id: 3, notes: ["G", "G", "F", "F", "F", "E", "E", "D"], lyric: "Bahar nikalo toh mar jayegi," },

  { id: 4, notes: ["C", "C", "G", "G", "A", "A", "G"], lyric: "Machili jal ki rani hai," },
  { id: 5, notes: ["F", "F", "E", "E", "D", "D", "C"], lyric: "Jeevan uska paani hai," },
];

/* ✅ Lyric groupings (4 groups per line)
   NOTE: Line 4 group 2 covers 3 notes now: F F F
*/
const LEVEL4_LYRIC_GROUPS = [
  ["Mach-li", "jal ki", "ra-ni", "hai"],
  ["Jee-van", "us-ka", "paa-ni", "hai"],
  ["Haath la-", "gao toh", "dar ja-", "yegi"],

  // ✅ UPDATED grouping per your mapping
  ["Bahar-ni", "kalo-toh", "mar jaye", "gi"],

  ["Mach-li", "jal ki", "ra-ni", "hai"],
  ["Jee-van", "us-ka", "paa-ni", "hai"],
];

/* ✅ Note index groups
   Line 4 changed from (2|2|2|1) to (2|3|2|1)
*/
const LEVEL4_NOTE_GROUP_INDEXES = [
  // Line 1
  [{ start: 0, end: 1 }, { start: 2, end: 3 }, { start: 4, end: 5 }, { start: 6, end: 6 }],
  // Line 2
  [{ start: 0, end: 1 }, { start: 2, end: 3 }, { start: 4, end: 5 }, { start: 6, end: 6 }],
  // Line 3
  [{ start: 0, end: 1 }, { start: 2, end: 3 }, { start: 4, end: 5 }, { start: 6, end: 6 }],

  // ✅ Line 4 (8 notes): [GG] [FFF] [EE] [D]
  [{ start: 0, end: 1 }, { start: 2, end: 4 }, { start: 5, end: 6 }, { start: 7, end: 7 }],

  // Line 5
  [{ start: 0, end: 1 }, { start: 2, end: 3 }, { start: 4, end: 5 }, { start: 6, end: 6 }],
  // Line 6
  [{ start: 0, end: 1 }, { start: 2, end: 3 }, { start: 4, end: 5 }, { start: 6, end: 6 }],
];

/* ─────────────────────────────────────────────
   HERO (NO overlay text)
   + Emoji burst:
     - correct: 💤 (Zzz)
     - wrong: 🔥
   Also: keep dragon more in focus (bias crop to top-right)
   ───────────────────────────────────────────── */

function Level4Hero({ backgroundImage, noteEvent }) {
  const [bursts, setBursts] = useState([]);
  const idRef = useRef(1);
  const lastEventRef = useRef(null);

  const safeRand = useMemo(() => {
    const seed = Math.random() * 9999;
    return () => {
      const x = Math.sin(seed + idRef.current * 12.9898) * 43758.5453;
      return x - Math.floor(x);
    };
  }, []);

  useEffect(() => {
    if (!noteEvent) return;
    if (noteEvent === lastEventRef.current) return;

    lastEventRef.current = noteEvent;

    const isHit = noteEvent === "hit";
    const emoji = isHit ? "💤" : "🔥";

    const id = idRef.current++;
    const left = 18 + safeRand() * 64;
    const top = 18 + safeRand() * 52;

    const burst = { id, emoji, left, top, isHit };
    setBursts((prev) => [...prev, burst]);

    const t = setTimeout(() => {
      setBursts((prev) => prev.filter((b) => b.id !== id));
    }, 750);

    return () => clearTimeout(t);
  }, [noteEvent, safeRand]);

  return (
    <div className="relative w-full max-w-3xl mx-auto rounded-[22px] sm:rounded-[24px] shadow bg-slate-900/5 overflow-hidden">
      {backgroundImage && (
        <img
          src={backgroundImage}
          alt=""
          className="w-full h-36 sm:h-56 object-cover object-right object-top"
          style={{
            // extra bias to keep Doron in frame (top-right)
            objectPosition: "88% 12%",
          }}
          draggable={false}
        />
      )}

      {bursts.map((b) => (
        <div
          key={b.id}
          className="tt-burst"
          style={{ left: `${b.left}%`, top: `${b.top}%` }}
          aria-hidden="true"
        >
          <span className={"tt-burst-emoji " + (b.isHit ? "tt-burst-hit" : "tt-burst-miss")}>
            {b.emoji}
          </span>
          {b.isHit && <span className="tt-burst-zzz">Zzz</span>}
        </div>
      ))}

      <style>{`
        .tt-burst{
          position:absolute;
          transform: translate(-50%, -50%);
          pointer-events:none;
          z-index: 5;
        }
        .tt-burst-emoji{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          font-size: 34px;
          filter: drop-shadow(0 6px 10px rgba(0,0,0,0.25));
          animation: ttFloatUp 750ms cubic-bezier(.2,.9,.2,1) forwards;
          will-change: transform, opacity;
        }
        .tt-burst-zzz{
          display:inline-block;
          margin-left: 6px;
          font-weight: 900;
          font-size: 18px;
          color: rgba(255,255,255,0.95);
          text-shadow: 0 6px 12px rgba(0,0,0,0.35);
          animation: ttFloatUp 750ms cubic-bezier(.2,.9,.2,1) forwards;
        }
        @keyframes ttFloatUp{
          0%   { transform: translateY(0px) scale(1); opacity: 1; }
          70%  { transform: translateY(-26px) scale(1.18); opacity: 1; }
          100% { transform: translateY(-46px) scale(1.05); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default function Level4() {
  const navigate = useNavigate();

  return (
    <LevelTemplate
      levelId={4}
      currentLevelForSidebar={4}
      videoSrc={LEVEL4_VIDEO_SRC} // ✅ STREAMING NOW
      startLabel="Start Game"
      melodyLines={LEVEL4_MELODY_LINES}
      lyricGroupsPerLine={LEVEL4_LYRIC_GROUPS}
      noteGroupIndexesPerLine={LEVEL4_NOTE_GROUP_INDEXES}
      backgroundImage={LEVEL4_HERO_IMAGE}
      heroImageFit="cover"
      renderHero={(heroProps) => (
        <Level4Hero
          backgroundImage={heroProps.backgroundImage}
          noteEvent={heroProps.noteEvent}
        />
      )}
      completionTitle="Machli Star unlocked!"
      completionBody={
        <>
          You played the spell perfectly 🎶✨ Grab your{" "}
          <span className="font-semibold text-[#4F46E5]">Machli Star</span> and
          continue the Star Quest!
        </>
      }
      // ✅ SUCCESS TRIGGER (Level-4 completed + star "L4" collected)
      onComplete={() => {
        TrophyStorage.markLevelAndStar(4); // marks level "4" + star "L4"
      }}
      onNextLevel={() => navigate("/level-5")}
    />
  );
}
